import {
  BAGGAGE_FEATURE_NAMES,
  BRANDED_FARE_FEATURES_CONFIG,
  CHANGEABLE_TICKET_FEATURES,
  REFUNDABLE_TICKET_FEATURES
} from "@flights/branded-fares";
import {
  BrandedFareClientFeature,
  BrandedFareFeatureConfig,
  BrandedFareFeatureGroup,
  UIBrandedFareFeature,
  UIBrandedFareFeatureAvailability,
  UIBrandedFareInfo
} from "@flights/types";
import flights_apex_web_show_checked_baggage_exclusions from "../../utils/experiments/apex/flights_apex_web_show_checked_baggage_exclusions";

type RenderFeaturesOptions = {
  includeSellableFeatures?: boolean;
  includeExcludedFeatures?: boolean;
  includeSellableBaggageFeatures?: boolean;
  includeSellableSeatsFeatures?: boolean;
  isPreCheckAndPay?: boolean;
};

const boardingAndFlightCategories = ["CHECK_IN", "CS", "FEE", "DETAILS", "BAGGAGE"];
const baggageFeatureNames = Object.values(BAGGAGE_FEATURE_NAMES);

export const getBoardingAndFlightFeatures = (features: UIBrandedFareFeature[]) =>
  features.filter(
    (feature) =>
      feature.category &&
      boardingAndFlightCategories.includes(feature.category) &&
      // There are features that have category "BAGGAGE" but are not actual baggage ancillaries, like priority baggage.
      // We want to show those baggage related benefits, but we don't want to show the actual baggage ancillaries here.
      !baggageFeatureNames.includes(feature.featureName)
  );

export const getFeaturesToRender = (brandedFareInfo: UIBrandedFareInfo, options: RenderFeaturesOptions = {}) => {
  const sellable = getWithAvailability(brandedFareInfo.sellableFeatures, "SELLABLE");
  const nonIncluded = getWithAvailability(brandedFareInfo.nonIncludedFeatures, "NOT_INCLUDED");
  const included = getWithAvailability(brandedFareInfo.features, "INCLUDED");
  const includedFeatures = filterRenderableFeatures(included, sellable, options);
  const nonIncludedFeatures = filterRenderableFeatures(nonIncluded, [], options);

  return { includedFeatures, nonIncludedFeatures };
};

export const mergeFeaturesInOrder = <T extends UIBrandedFareFeature>(a: T[], b: T[]): T[] => {
  const source = [...a, ...b];
  return BRANDED_FARE_FEATURES_CONFIG.flatMap((config) => findFeatures(config, source));
};

const filterRenderableFeatures = (
  features: UIBrandedFareFeature[],
  alternativeFeatures: UIBrandedFareFeature[] = [],
  options: RenderFeaturesOptions = {}
) => {
  const compressed = compressSimilarFeatures(features);
  const alternativeCompressed = compressSimilarFeatures(alternativeFeatures);
  const renderableFeatures: BrandedFareClientFeature[] = [];

  for (const config of BRANDED_FARE_FEATURES_CONFIG) {
    const { group, icon } = config;

    let foundFeatures = findFeatures(config, compressed);
    if (foundFeatures.length === 0) foundFeatures = findFeatures(config, alternativeCompressed);

    for (const found of foundFeatures) {
      if (!found?.label) continue;

      const { availability, label } = found;

      if (availability === "SELLABLE" && !shouldShowSellableFeature(options, group)) continue;
      if (availability === "NOT_INCLUDED" && !shouldShowExcludedFeature(found, options, group)) continue;

      renderableFeatures.push({ ...found, label, group, icon });
    }
  }

  return renderableFeatures;
};

const shouldShowSellableFeature = (options: RenderFeaturesOptions, group?: BrandedFareFeatureGroup) => {
  if (group === "BAGGAGE" && options.includeSellableBaggageFeatures) return true;
  if (group === "FLEXIBILITY") return true;
  if (group === "ON_THE_FLIGHT" && options.includeSellableSeatsFeatures) return true;
  return options.includeSellableFeatures && options.isPreCheckAndPay;
};

const shouldShowExcludedFeature = (
  feature: UIBrandedFareFeature,
  options: RenderFeaturesOptions,
  group?: BrandedFareFeatureGroup
) =>
  group === "FLEXIBILITY" ||
  (options.includeExcludedFeatures && (group !== "BAGGAGE" || options.isPreCheckAndPay)) ||
  (feature.featureName === "CHECK_BAGGAGE" &&
    process.env.BUILD_TARGET === "client" &&
    !!flights_apex_web_show_checked_baggage_exclusions.trackWithDefaultStage());

const getWithAvailability = <T extends Pick<UIBrandedFareFeature, "availability">>(
  features: T[] | undefined,
  availability: UIBrandedFareFeatureAvailability
) => (features || []).filter((f) => f.availability === availability);

const findFeatures = <T extends UIBrandedFareFeature>(config: BrandedFareFeatureConfig, features: T[]): T[] =>
  features.filter((f) => f.featureName === config.name);

const compressSimilarFeatures = (features: UIBrandedFareFeature[]): UIBrandedFareFeature[] => {
  const compressedFeatures: UIBrandedFareFeature[] = [];
  let changeable = false;
  let refundable = false;

  features.forEach((feature) => {
    const { featureName } = feature;
    if (CHANGEABLE_TICKET_FEATURES.includes(featureName)) {
      if (!changeable) {
        compressedFeatures.push(feature);
        changeable = true;
      }
    } else if (REFUNDABLE_TICKET_FEATURES.includes(featureName)) {
      if (!refundable) {
        compressedFeatures.push(feature);
        refundable = true;
      }
    } else {
      compressedFeatures.push(feature);
    }
  });

  return compressedFeatures;
};
